<script lang="ts">
	import Button from "../components/Button.svelte";
	import ArrowRight from "../components/icon/ArrowRight.svelte";
	import Input from "../components/Input.svelte";
	import SvelteMarkdown from "svelte-markdown";
	import FriendlyCaptcha from "../components/FriendlyCaptcha.svelte";

	import { defaults } from "./data/selfAssessmentDefaultOptions";
	import { preserveCursor } from "../util/handlers";
	import { sendDYEvents } from "./utils/dy";
	import { getCookie } from "../util/cookie";

	import { onMount } from 'svelte';
	import { replaceHiddenInputValues } from '../../src/modules/replaceHiddenInputValues';

	onMount(() => {
	  replaceHiddenInputValues();
	});

	const nameFieldName = "name";

	export let qnas: string[] = [];
	export let onSuccess = (event: SubmitEvent) => {};
	export let options: typeof defaults = defaults;

	let lastname = "";
	let firstname = "";
	let form: HTMLFormElement | null = null;
	let disabled = false;
	let solution = "";

	let values = {
	[nameFieldName]: "",
	[options.emailFieldName]: "",
	[options.phoneNumberFieldName]: "",
	marketingConsent: false,
	};

	let validation = {
	[nameFieldName]: true,
	[options.emailFieldName]: true,
	[options.phoneNumberFieldName]: true,
	};

	// For some reason we want to show validation errors in the input...
	let preValidationValues = {
	[nameFieldName]: "",
	[options.emailFieldName]: "",
	[options.phoneNumberFieldName]: "",
	};

	$: {
	const parts = values[nameFieldName].split(" ");
	firstname = parts.shift() || "";
	lastname = parts.join(" ");
	}

	function getUtmTagsFromUrlOrCookie(): [string, string][] {
	let utmParams = decodeURIComponent(window.location.search)
	.substr(1)
	.split("&")
      .filter((p) => p.indexOf("utm_") > -1);

    if (utmParams.length > 0) {
      return utmParams.map((param) => param.split("=") as [string, string]);
    }

    const utmCookie = getCookie("sec_utm");

    if (utmCookie.length > 0) {
      return utmCookie
        .split("&")
        .map((param) => param.split("=") as [string, string]);
    }

    return [];
  }

  async function postForm(
    token: string
  ): Promise<{ success: boolean; message: string }> {
	if (options.skipPost) {
		return Promise.resolve({ success: true, message: "From skipPost" });
	}

    const formData = new FormData(form || undefined);
	// Remove spaces from the phone number before appending it to the formData
	const phoneNumber = values[options.phoneNumberFieldName];
	const sanitizedPhoneNumber = typeof phoneNumber == "string" ? phoneNumber.replace(/\s+/g, '') : "";
	formData.set(options.phoneNumberFieldName, sanitizedPhoneNumber);
    formData.set("token", token);
    formData.set("contentReference", options.id || "");
    formData.set("sent_from_form", "self-assessment");
    formData.set("reasonofContact", options.reasonOfContact);
	formData.set("marketocampaign", options.marketoCampaign);
    const utmParams = getUtmTagsFromUrlOrCookie();

    utmParams.forEach(([key, value]: [string, string]) => {
      formData.set(key, value);
    });

    // this whole exercise because multiple values may share one 'Form Field' in a 'Form Capture'
    const postValues: { [key: string]: string[] } = {};
    pushPostValues(postValues);

    // Update form data with the grouped values
    Object.keys(postValues).forEach((key) => {
      formData.set(key, postValues[key].join("\n"));
    });

    // prepare for x-www-form-urlencoded POST
    const entries = [...formData.entries()].map(
      (e) =>
        encodeURIComponent(e[0]) + "=" + encodeURIComponent(e[1].toString())
    );

    const res = await fetch("/api/clickdimensions/send", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      body: entries.join("&"),
	});

	  return res.json();
	}

	  function pushPostValues(postValues: { [key: string]: string[] }) {
	  if (!!(window as any).DY) {
	  pushValueToKey(
	  options.geoLocationFieldName,
	  (window as any).DY.geoCity,
	  postValues
	  );
	  }

	  if (!!(window as any).ga) {
	  const clientId = (window as any).ga.getAll()[0].get("clientId");
	  if (clientId) {
	  pushValueToKey(options.gaCidFieldName, clientId, postValues);
	  }
	  }

	  pushValueToKey(options.sentFromPageFieldName, location.href, postValues);

	  if (qnas.length > 0) {
	  pushValueToKey(
	  options.questionAnswersFieldName,
	  qnas.join("\n"),
	  postValues
	  );
	  }
	  }

	  function pushValueToKey(
	  key: string,
	  value: string,
	  postValues: { [key: string]: string[] }
	  ) {
	  let values = postValues[key] || [];
	  values.push(value);
	  postValues[key] = values;
	  }

	  async function handleSubmit(e: SubmitEvent) {
	  if (disabled) return false;
	  disabled = true;

	  validate();

	  if (Object.values(validation).some((v) => !v)) {
	  disabled = false;
	  return false;
	  }

	  let token = options.siteKey ? solution : "";
	  const { success, message } = await postForm(token);

	if (success) {
	  onSuccess(e);
	  sendDYEvents(options.category);
	  } else {
	  disabled = false;
	  console.error({ message });
	  }
	}

	function validate() {
	  const name = values[nameFieldName];
	  const email = values[options.emailFieldName];
	  const phone = values[options.phoneNumberFieldName];
	  const marketingConsent = values.marketingConsent;
	  if (name.length < 3) {
      setInvalidField(name, nameFieldName, options.invalidNameText);
    }

    if (typeof email == "string" && !emailValid(email)) {
      setInvalidField(email, options.emailFieldName, options.invalidEmailText);
    }

    if (typeof phone == "string" && phone.length < 5) {
      setInvalidField(
        phone,
        options.phoneNumberFieldName,
        options.invalidPhoneText
      );
    } 
   }
	function emailValid(email: string) {
		const re =
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }
  const handleInputFocus = (e: FocusEvent) => {
    const { name } = e.target as HTMLInputElement;

    if (validation[name]) return;

    values = {
      ...values,
      [name]: preValidationValues[name],
    };

    validation = {
      ...validation,
      [name]: true,
    };
  };
function setInvalidField(
  value: string,
  fieldName: string,
  errorText: string
) {
preValidationValues[fieldName] = value;
  // If the field is marketingConsent, keep it as true/false (don't overwrite with the error message)
  if (fieldName !== "marketingConsent") {
    values = {
      ...values,
      [fieldName]: errorText,  // For non-checkbox fields, store error message in values
    };
  } else {
    // For marketingConsent, store only a boolean value (true/false)
    values = {
      ...values,
      [fieldName]: false,  // Set as false on error
    };
  }

  validation = {
    ...validation,
    [fieldName]: false,  // Mark this field as invalid
  };
}
</script>

<form
  novalidate
  bind:this={form}
  method="POST"
  on:submit|preventDefault={handleSubmit}
>
  <Input
    type="hidden"
    name={options.firstNameFieldName}
    bind:value={firstname}
  />
  <Input type="hidden" name={options.lastNameFieldName} bind:value={lastname} />
  <div class="svelte-click-dimensions-form-container">
    <div class="svelte-click-dimensions-form">
      <div class="formControl">
        <Input
          bind:value={values[nameFieldName]}
          name={nameFieldName}
          error={!validation[nameFieldName]}
          placeholder={`${options.nameLabel}*`}
          required={true}
          on:focus={preserveCursor(handleInputFocus)}
        />
      </div>
      <div class="flexClear-md" />
      <div class="formControl">
        <Input
          bind:value={values[options.phoneNumberFieldName]}
          name={options.phoneNumberFieldName}
          error={!validation[options.phoneNumberFieldName]}
          placeholder={`${options.phoneNumberLabel}*`}
          required={true}
          on:focus={preserveCursor(handleInputFocus)}
        />
      </div>
      <div class="flexClear-md" />
      <div class="formControl">
        <Input
          bind:value={values[options.emailFieldName]}
          name={options.emailFieldName}
          error={!validation[options.emailFieldName]}
          placeholder={`${options.emailLabel}*`}
          required={true}
          on:focus={preserveCursor(handleInputFocus)}
        />
      </div>
      <div class="flexClear-md" />

		<!-- Hidden input for source campaign -->
		    <Input name="source_campaign" type="hidden" value="SOURCECAMPAIGN" />

		<div class="formControl">
        <Button
          disabled={disabled || (!solution && !!options.siteKey)}
          type="primary"
          text={options.submitText}><ArrowRight /></Button
        >
      </div>
		<div class="flexClear-lg" />
		{#if options.marketingConsent}
		<div class="formControl">
			<Input
				  type="checkbox"
				  name="marketingConsent"
				  bind:value={values.marketingConsent}
      		/>
			<label for="checkbox" class="checkboxLabel">
				{#if options.marketingConsent.length > 0}
				<SvelteMarkdown source={options.marketingConsent} />
				{/if}
			</label>
		</div>
		{/if}
		<div class="flexClear-lg" />
	{#if options.siteKey}
	<div class="formControl">
			<FriendlyCaptcha bind:solution={solution} siteKey={options.siteKey} language={options.language} />
		</div>
		{/if}
	</div>
    <div class="formInformation">
      <span class="requiredFields">* {options.requiredFieldsText}</span>
      {#if options.legalText.length > 0}
        <SvelteMarkdown source={options.legalText} />
      {/if}
    </div>
  </div>
</form>

<style lang="scss">
	.formInformation {
	text-align: left;

	.requiredFields {
	text-align: left;
	display: inline-block;
	margin-top: 0;
	margin-bottom: 1rem;
	width: 100%;
	}
	}
	.checkboxLabel {
	display: flex;
	align-items: center; /* Align checkbox and label text vertically */
	cursor: pointer; /* Makes it clear the label is clickable */
	}

	.svelte-click-dimensions-form {
	display: flex;
	flex-direction: row;
	justify-content: center;
	flex-wrap: wrap;

	.formControl {
	margin: 0 1rem 1rem 0;
	display: flex;
	align-items: center;
	}

	@media screen and (max-width: 980px) {
	.flexClear-md {
	flex-basis: 100%;
	height: 0;
	}
	}
	}
	@media screen and (max-width: 980px) {
	.flexClear-md {
	flex-basis: 100%;
	height: 0;
	}
	}

	@media screen and (min-width: 980px) {
	.flexClear-lg {
	margin-top: var(--spacing__24);
	flex-basis: 100%;
	height: 0;
	}
	}
</style>
